<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import actions from "~/actions";
import AgentConfigModal from "~/components/AgentConfigModal.vue";
import AgentWorkflowModal from "~/components/AgentWorkflowModal.vue";
import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import SimpleTable from "~/components/dumb/SimpleTable.vue";
import { TEMPLATE_AGENT_CONFIGS } from "~/constants/agents/configs";
import { USER_KIND_CUSTOM } from "~/constants/userKinds";
import { AgentIcon, UsersRemoveIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { ButtonSize, ButtonStyle, DialogMode, DropdownMenuItemKind, Placement } from "~/shared/enums";
import type { AgentConfig, User } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore } from "~/stores";
import { agentComparator } from "~/utils/comparator";
import { when } from "~/utils/wait";
import ActionsCell from "~/views/settings/UserCells/ActionsCell.vue";
import AvatarCell from "~/views/settings/UserCells/AvatarCell.vue";
import StatusCell from "~/views/settings/UserCells/StatusCell.vue";
import WorkflowCell from "~/views/settings/UserCells/WorkflowCell.vue";
import { useSettingsFooter } from "~/views/settings/useSettings";

const DELETE_AGENT_DIALOG_DESCRIPTION =
  "Permanently deleting an agent can't be undone. Are you sure you want to proceed?";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const table = ref<ComponentExposed<typeof SimpleTable<User>> | null>(null);
const deleteAgentModal = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const agentToRemove = ref<User | null>(null);
const agentInConfigModal = ref<User | undefined>();
const agentInWorkflowModal = ref<User | undefined>();

const allAgents = computed(() =>
  dataStore.getUserList({ excludeNonagents: true, includeDisabledAgents: true }).sort(agentComparator)
);

const getAvatarCell = (agent: User) =>
  table.value?.getCell("Agent", agent.duid) as unknown as InstanceType<typeof AvatarCell> | undefined;

const openWorkflowModal = (agent: User) => {
  agentInWorkflowModal.value = agent;
};

const closeWorkflowModal = () => {
  agentInWorkflowModal.value = undefined;
};

const openConfigModal = (agent: User) => {
  agentInConfigModal.value = agent;
};

const closeConfigModal = () => {
  agentInConfigModal.value = undefined;
};

let createAgent: (config?: AgentConfig) => Promise<void>;

const existingAgentNames = computed(() => new Set(allAgents.value.map((e) => e.name)));
const addAgentDropdownItems = computed(() => {
  const templateItems = TEMPLATE_AGENT_CONFIGS.map((config) => ({
    title: config.name,
    hidden: existingAgentNames.value.has(config.name),
    kind: DropdownMenuItemKind.BUTTON,
    icon: config.icon,
    onClick: () => createAgent(config),
  }));

  return [
    ...templateItems,
    {
      title: "Custom",
      hidden: false,
      kind: DropdownMenuItemKind.BUTTON,
      icon: AgentIcon,
      onClick: () => createAgent(),
    },
  ];
});
const onlyCustomAgent = computed(() => addAgentDropdownItems.value.filter((e) => !e.hidden).length === 1);
const addAgentDropdownSections = computed(() => [
  {
    title: "Type",
    items: addAgentDropdownItems.value,
  },
]);

createAgent = async (config?: AgentConfig) => {
  const partialAgent: Partial<User> = {};
  if (config) {
    partialAgent.kind = config.name;
    partialAgent.name = config.name;
    partialAgent.colorHex = config.color;
  }

  const agent = await dataStore.createAgent("", partialAgent);
  if (!config) {
    if (!onlyCustomAgent.value && dropdownMenu.value?.isOpen) {
      await when(computed(() => !dropdownMenu.value?.isOpen));
    }
    getAvatarCell(agent)?.startEditing();
    return;
  }

  openConfigModal(agent);
};

const deleteAgent = () => {
  if (!agentToRemove.value) {
    return;
  }

  dataStore.deleteAgent(agentToRemove.value);
};

const getContextMenuSections = (agent: User) =>
  actions.context.user(agent, (eventKind: string) => {
    switch (eventKind) {
      case "startEditing": {
        // eslint-disable-next-line no-restricted-syntax
        setTimeout(() => {
          getAvatarCell(agent)?.startEditing();
        }, 100);
        break;
      }
      case "openConfigModal": {
        openConfigModal(agent);
        break;
      }
      case "openWorkflowModal": {
        openWorkflowModal(agent);
        break;
      }
      case "deleteAgent": {
        agentToRemove.value = agent;
        deleteAgentModal.value?.openModal();
        break;
      }
      default: {
        throw new Error(`Unknown event kind: ${eventKind}`);
      }
    }
  });

const onContextMenu = (event: MouseEvent, agent: User) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }
  appStore.openContextMenu(event as PointerEvent, getContextMenuSections(agent));
};

const columns = computed(() => [
  {
    title: "Agent",
    hideTitle: true,
    cell: AvatarCell,
    getCellArgs: (agent: User) => ({
      onUpdateName: (name: string) => {
        if (!name) {
          if (!agent.name) {
            dataStore.deleteAgent(agent);
          }
          return;
        }
        dataStore.updateAgent({ duid: agent.duid, name });
      },
    }),
    classes: "w-full max-w-0 pr-2",
  },
  { title: "Status", cell: StatusCell, classes: "pr-2", headerClasses: "min-w-20 text-left" },
  {
    title: "Workflows",
    cell: WorkflowCell,
    getCellArgs: (agent: User) => ({ openWorkflowModal: () => openWorkflowModal(agent) }),
    classes: "w-28",
    headerClasses: "min-w-28 text-left",
  },
  {
    title: "Actions",
    hideTitle: true,
    cell: ActionsCell,
    getCellArgs: () => ({ getContextMenuSections }),
    classes: "w-6",
    headerClasses: "w-6",
  },
]);

const teammatesLink = makeLinkToSettingsRef("teammates");

const { setFooterContent } = useSettingsFooter();

onMounted(async () => {
  setFooterContent({ text: "Invite a teammate instead", link: teammatesLink.value });
  if (appStore.agentCreationType) {
    const agentType = appStore.agentCreationType;
    appStore.setAgentCreationType(null);

    const config =
      agentType === USER_KIND_CUSTOM ? undefined : TEMPLATE_AGENT_CONFIGS.find((e) => e.name === agentType);
    await createAgent(config);
  }
});
onUnmounted(() => {
  setFooterContent(null);
});
</script>

<template>
  <div>
    <ConfirmationDialog
      ref="deleteAgentModal"
      :mode="DialogMode.DELETE"
      :title="`Delete ${agentToRemove?.name ?? 'agent'}`"
      :description="DELETE_AGENT_DIALOG_DESCRIPTION"
      confirm-text="Delete"
      cancel-text="Cancel"
      :icon="UsersRemoveIcon"
      @confirm="deleteAgent" />

    <AgentConfigModal :agent="agentInConfigModal" @close="closeConfigModal" />

    <AgentWorkflowModal :agent="agentInWorkflowModal" @close="closeWorkflowModal" />

    <div class="relative flex flex-col">
      <div class="absolute -top-20 right-0">
        <DropdownMenu
          ref="dropdownMenu"
          :disabled="onlyCustomAgent"
          :sections="addAgentDropdownSections"
          :placement="Placement.BOTTOM_RIGHT">
          <Button
            :btn-style="ButtonStyle.PRIMARY"
            text="Add agent"
            :size="ButtonSize.LARGE"
            @click="onlyCustomAgent && createAgent()" />
        </DropdownMenu>
      </div>

      <SimpleTable ref="table" :columns="columns" :rows="allAgents" @context-menu="onContextMenu" />
    </div>
  </div>
</template>
